// Feedback command group — send a report ("Give feedback", "Report this").
// One slice of the appContract map; re-assembled in ../commands.ts.
//
// Every schema is strict: an unknown field is refused (QA-26). The text is
// left unbounded here so the domain can refuse it by name
// (`feedback_text_too_long`, CL-6: an honest paste can reach it); context
// strings are capped at the short-text ceiling, which only a crafted request
// can pass (the client clips each chip before showing it).
export { feedbackCommands, zFeedbackContextItem };
import * as D from "@bilda/core";
import { exhaustSwitchCase, Result } from "@bilda/utils";
import { z } from "zod";
import { contractError, defineContract } from "../shared.js";
const zContextText = z.string().max(D.Limits.SHORT_TEXT_MAX);
const zFeedbackContextItem = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("feedback_context_view"),
        view: zContextText,
    }),
    z.strictObject({
        type: z.literal("feedback_context_client"),
        version: zContextText,
        browser: zContextText,
        os: zContextText,
    }),
    z.strictObject({
        type: z.literal("feedback_context_error"),
        errorId: zContextText,
        summary: zContextText,
    }),
]);
const S = D.Feedback.State;
const toContextItem = (c) => {
    switch (c.type) {
        case "feedback_context_view":
            return { type: c.type, view: S.mkContextText(c.view) };
        case "feedback_context_client":
            return {
                type: c.type,
                version: S.mkContextText(c.version),
                browser: S.mkContextText(c.browser),
                os: S.mkContextText(c.os),
            };
        case "feedback_context_error":
            return {
                type: c.type,
                errorId: S.mkContextText(c.errorId),
                summary: S.mkContextText(c.summary),
            };
        default:
            return exhaustSwitchCase(c);
    }
};
const contactOf = (may) => may ? "contact_allowed" : "contact_not_allowed";
const feedbackCommands = {
    sendFeedbackReport: defineContract({
        effect: "command",
        summary: "Send feedback",
        description: `Sends a report from the signed-in person (taken from the session, never the body): a problem, an idea or a question, their own words (plain text, at most ${D.Limits.LONG_TEXT_MAX} characters), the context chips they kept (each at most once), and whether they may be contacted. Stored in the workspace; operators read it in the admin area. Refused by name: text over the ceiling (\`feedback_text_too_long\`), nothing to send (\`feedback_report_empty\`: blank text and no error attached), ${D.Limits.FEEDBACK_REPORTS_PER_DAY_MAX} reports already sent today (\`feedback_daily_limit_reached\`, CL-23).`,
        inputSchema: z.strictObject({
            kind: z.enum(["problem", "idea", "question"]),
            text: z.string().meta({
                description: "The person's own words, plain text. May be blank when an error is attached.",
            }),
            context: z.array(zFeedbackContextItem).max(3),
            mayContact: z.boolean(),
        }),
        refine: (w) => new Set(w.context.map((c) => c.type)).size === w.context.length
            ? Result.ok({
                kind: w.kind,
                text: S.mkFeedbackText(w.text),
                context: w.context.map(toContextItem),
                contact: contactOf(w.mayContact),
            })
            : Result.err(contractError("context", "each kind of context at most once")),
    }),
};
//# sourceMappingURL=feedback.js.map